import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { useAuth } from '../../contexts/AuthContext';
import { useClassDetail } from '@/data/queries/useClasses';
import { classDetailDtoToClassItem } from '@/utils/classUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Button } from '@/components/shared/ui';
import { FileText } from 'lucide-react';
import { cn } from '@/lib/cn';
import { nativePickerInputClassName, triggerNativePickerOnClick } from '@/components/shared/ui/input';
import SimulationResults from './SimulationResults';

type TabType = 'curriculum' | 'evaluation' | 'practice-status';

export default function StudentEvaluationResults(): React.ReactElement {
    const { t } = useTranslation('class');
    const { classId: classIdParam } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const basePath = '/student';
    const classId = parseInt((classIdParam ?? '').trim(), 10);
    const currentTab = 'evaluation';

    // 조회기간 필터
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    // 정렬 기준 필터
    const [sortCriteria, setSortCriteria] = useState('latest');
    
    // 선택된 시나리오
    const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(null);
    
    // PDF 생성 시 헤더 표시 여부
    const [showPdfHeader, setShowPdfHeader] = useState(false);

    // 멀티 계정 지원: currentAccount.accountId 사용
    // accountId 형식: "acc_123" → 숫자만 추출
    const currentUserId = useMemo(() => {
        if (user?.currentAccount?.accountId) {
            const accountId = user.currentAccount.accountId;
            // "acc_123" → "123" 추출
            const match = accountId.match(/\d+/);
            return match ? match[0] : user?.userId?.toString();
        }
        return user?.userId?.toString();
    }, [user]);

    // 클래스 정보 가져오기
    const {
        data: classDetailDto,
        isError,
    } = useClassDetail(classId, {
        enabled: classId > 0,
    });

    const classItem = classDetailDto ? classDetailDtoToClassItem(classDetailDto) : null;

    // 시나리오 목록 (curriculum에서 scenario 타입만)
    const scenarioList = useMemo(
        () => classItem?.curriculum?.filter(item => item.type === 'scenario') || [],
        [classItem?.curriculum]
    );

    // 첫 로딩 시 첫 번째 시나리오 자동 선택
    useEffect(() => {
        if (scenarioList.length > 0 && selectedScenarioId === null) {
            setSelectedScenarioId(scenarioList[0].id);
        }
    }, [scenarioList, selectedScenarioId]);

    // 시나리오 클릭 핸들러 - 같은 페이지에서 오른쪽에 표시
    const handleScenarioClick = (scenarioId: number) => {
        setSelectedScenarioId(scenarioId);
    };

    const backToClassDetailPath = (): string => {
        return `${basePath}/my-classes/${classId}`;
    };

    const handleTabChange = (tab: TabType) => {
        const pathPrefix = `${basePath}/my-classes`;

        if (tab === 'curriculum') {
            // 커리큘럼 탭으로 이동
            if (classItem?.curriculum?.length) {
                const firstSession = classItem.curriculum[0];
                navigate(`${pathPrefix}/${classId}/curriculum/${firstSession.id}`);
            }
        } else if (tab === 'evaluation') {
            // 이미 평가결과이므로 아무것도 안 함
            return;
        } else if (tab === 'practice-status') {
            // 실습현황 탭으로 이동
            navigate(`${pathPrefix}/${classId}/practice-status`);
        }
    };

    const allTabs = [
        { id: 'curriculum', label: t('session.tabCurriculum') },
        { id: 'evaluation', label: t('session.tabEvaluation') },
        { id: 'practice-status', label: t('session.tabPracticeStatus') },
    ] as const;

    const renderCenterContent = () => {
        return (
            <div className="flex gap-2">
                {allTabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id as TabType)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            currentTab === tab.id
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    const handleDownloadPdf = async () => {
        if (!selectedScenarioId) {
            alert('시나리오를 먼저 선택해주세요.');
            return;
        }

        try {
            setShowPdfHeader(true);
            await new Promise(resolve => setTimeout(resolve, 100));

            const captureElement = document.querySelector('.result-capture-area');
            if (!captureElement) {
                console.error('캡처할 영역을 찾을 수 없습니다.');
                setShowPdfHeader(false);
                return;
            }

            const canvas = await html2canvas(captureElement as HTMLElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: 1440,
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pageWidth - margin * 2;

            const imgWidth = contentWidth;
            const imgHeight = (canvas.height * contentWidth) / canvas.width;

            if (imgHeight <= pageHeight - margin * 2) {
                pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
            } else {
                let remainingHeight = imgHeight;
                let sourceY = 0;
                let pageCount = 0;

                while (remainingHeight > 0) {
                    if (pageCount > 0) {
                        pdf.addPage();
                    }

                    const availableHeight = pageHeight - margin * 2;
                    const sourceHeight = (availableHeight * canvas.width) / contentWidth;

                    const canvasSlice = document.createElement('canvas');
                    canvasSlice.width = canvas.width;
                    canvasSlice.height = Math.min(sourceHeight, canvas.height - sourceY);
                    const ctx = canvasSlice.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(
                            canvas,
                            0,
                            sourceY,
                            canvas.width,
                            canvasSlice.height,
                            0,
                            0,
                            canvas.width,
                            canvasSlice.height
                        );
                    }

                    const sliceData = canvasSlice.toDataURL('image/png');
                    const sliceHeight = (canvasSlice.height * contentWidth) / canvas.width;
                    pdf.addImage(sliceData, 'PNG', margin, margin, imgWidth, sliceHeight);

                    sourceY += canvasSlice.height;
                    remainingHeight -= availableHeight;
                    pageCount++;
                }
            }

            const selectedScenario = scenarioList.find(s => s.id === selectedScenarioId);
            const studentName = user?.name || '학생';
            const fileName = `${studentName}_${selectedScenario?.name || '결과'}_${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(fileName);

            setShowPdfHeader(false);
        } catch (error) {
            console.error('PDF 생성 중 오류 발생:', error);
            setShowPdfHeader(false);
        }
    };

    const renderRightContent = () => {
        return (
            <div className="flex flex-wrap items-center gap-1.5 justify-end">
                {/* 조회기간 */}
                <div className="flex items-center gap-1.5">
                    <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-1.5 py-0.5 h-7">
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            onClick={triggerNativePickerOnClick}
                            className={cn(
                                'text-xs font-medium text-gray-700 focus:outline-none bg-transparent w-24',
                                nativePickerInputClassName,
                            )}
                        />
                    </div>
                    <div className="w-1 h-px bg-gray-300" />
                    <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-1.5 py-0.5 h-7">
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            onClick={triggerNativePickerOnClick}
                            className={cn(
                                'text-xs font-medium text-gray-700 focus:outline-none bg-transparent w-24',
                                nativePickerInputClassName,
                            )}
                        />
                    </div>
                </div>

                {/* 정렬 기준 + 다운로드 버튼 그룹 */}
                <div className="flex items-center gap-1.5">
                    <Select value={sortCriteria} onValueChange={setSortCriteria}>
                        <SelectTrigger className="w-[120px] h-7 text-xs font-bold bg-white border-gray-200">
                            <SelectValue placeholder={t('simulationResults.criteriaPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="latest">{t('simulationResults.criteriaLatest')}</SelectItem>
                            <SelectItem value="highest">{t('simulationResults.criteriaHighest')}</SelectItem>
                            <SelectItem value="lowest">{t('simulationResults.criteriaLowest')}</SelectItem>
                            <SelectItem value="all">{t('simulationResults.criteriaAll')}</SelectItem>
                        </SelectContent>
                    </Select>

                    <div className="h-5 w-px bg-slate-200" />

                    <Button variant="primary" size="sm" onClick={handleDownloadPdf} className="gap-1 h-7 text-xs px-2">
                        <FileText size={12} />
                        PDF
                    </Button>
                </div>
            </div>
        );
    };

    if (!classDetailDto && !isError) {
        return (
            <FullScreenLayout
                header={<FullScreenHeader title="…" onBack={() => navigate(backToClassDetailPath())} />}
            >
                <div className="p-8 text-gray-500">{t('myClass.loading')}</div>
            </FullScreenLayout>
        );
    }

    if (isError || !classItem) {
        return (
            <FullScreenLayout
                header={<FullScreenHeader title={t('evaluation.title')} onBack={() => navigate(backToClassDetailPath())} />}
            >
                <div className="p-8 text-gray-500">{t('detail.classNotFound')}</div>
            </FullScreenLayout>
        );
    }

    return (
        <FullScreenLayout
            header={
                <FullScreenHeader
                    title={classItem.title}
                    onBack={() => navigate(backToClassDetailPath())}
                    centerContent={renderCenterContent()}
                    rightContent={renderRightContent()}
                />
            }
        >
            <div className="flex flex-1 min-h-0">
                {/* 좌측 사이드바 */}
                <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
                    {/* 헤더 (고정) */}
                    <div className="p-4 border-b border-gray-200">
                        <h2 className="text-lg font-bold text-gray-900">
                            {t('evaluation.title')} <span className="text-sm font-normal text-gray-500">({scenarioList.length})</span>
                        </h2>
                    </div>
                    {/* 시나리오 리스트 (스크롤) */}
                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="space-y-1">
                            {scenarioList.length === 0 ? (
                                <div className="text-sm text-gray-500 text-center py-4">
                                    {t('evaluation.noScenarios')}
                                </div>
                            ) : (
                                scenarioList.map((scenario) => (
                                    <button
                                        key={scenario.id}
                                        onClick={() => handleScenarioClick(scenario.id)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                            selectedScenarioId === scenario.id
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <div className="font-medium">{scenario.name}</div>
                                        {scenario.practiceMode && (
                                            <div className="text-xs text-gray-500 mt-0.5">{scenario.practiceMode}</div>
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* 우측 메인 영역 - 상세결과 */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                    {selectedScenarioId && currentUserId ? (
                        <SimulationResults
                            key={`${classId}-${selectedScenarioId}-${currentUserId}`}
                            overrideParams={{
                                classId: classId.toString(),
                                sessionId: selectedScenarioId.toString(),
                                userId: currentUserId,
                            }}
                            hideLayout={true}
                            startDate={startDate}
                            endDate={endDate}
                            sortCriteria={sortCriteria}
                            showPdfHeader={showPdfHeader}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full bg-gray-50">
                            <div className="text-center">
                                <p className="text-gray-500 text-lg mb-2">왼쪽에서 시나리오를 선택하세요</p>
                                <p className="text-gray-400 text-sm">시나리오를 클릭하면 평가결과를 확인할 수 있습니다</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </FullScreenLayout>
    );
}
